<template>
	<div v-if="productVariants && productVariants.length > 1">
		<span class="text-xs font-bold lg:text-sm">Select size</span>
	</div>

	<div class="col-span-12 xl:col-span-8">
		<div
			v-if="productVariants && productVariants.length > 0"
			class="mb-5 grid grid-cols-12 content-center gap-4"
		>
			<div class="col-span-6" v-for="productVariant in productVariants">
				<a
					:href="productVariant.url"
					:class="[
						'hover:bg-gray-50 inline-flex w-full cursor-pointer items-center justify-between rounded-lg border-2 p-3 text-black hover:text-gray-600',
						productVariant.selected
							? 'border-stone bg-stone-200 text-gray-600'
							: 'border-2 border-stone bg-white',
					]"
				>
					{{ getProductVariantLabel(productVariant) }}
				</a>
			</div>
		</div>

		<div>
			<span v-if="multipleItemsPerPack" class="block">
				<span class="font-bold">Set of {{ itemsPerPack }} only</span>
			</span>

			<span class="mt-2 block">
				<span :class="['text-4xl', unitPriceClass]">
					<span class="font-bold">£{{ this.unitPrice.toFixed(2) }}</span> inc VAT</span
				>
				<span v-if="showPreviousPrice" class="ml-2 text-2xl line-through">
					£{{ this.wasPrice.toFixed(2) }}</span
				>
			</span>

			<span v-if="multipleItemsPerPack" class="mt-2 block">
				<span
					>(<span class="font-bold">{{ itemPriceLabel }}</span> per piece)</span
				>
			</span>
		</div>

		<div class="mt-3">
			<div v-if="maxQuantityOrderable > 0">
				<div class="float-left">
					<button
						@click="decrementQuantity"
						class="disabled:cursor-not-allowed disabled:opacity-50"
						:disabled="quantity <= 1"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="51"
							height="47"
							viewBox="0 0 51 47"
						>
							<rect
								id="Rectangle_827"
								data-name="Rectangle 827"
								width="51"
								height="47"
								fill="#e3ded0"
								opacity="0.401"
							/>
							<g
								id="Group_767"
								data-name="Group 767"
								transform="translate(-745.246 -927.002) rotate(45)"
								opacity="0.5"
							>
								<line
									id="Line_190"
									data-name="Line 190"
									x1="12.628"
									y2="12.628"
									transform="translate(1211.5 121.5)"
									fill="none"
									stroke="#707070"
									stroke-width="1.5"
								/>
							</g>
						</svg>

						<span class="sr-only">{{ decrementQuantityLabel }}</span>
					</button>
				</div>

				<div class="float-left">
					<div class="inline-grid h-12 w-12 place-items-center">
						<input
							type="number"
							min="1"
							:max="maxQuantityOrderable"
							class="h-full w-full text-center"
							:value="quantity"
						/>
					</div>
				</div>

				<div class="float-left mr-2">
					<button
						@click="incrementQuantity"
						class="disabled:cursor-not-allowed disabled:opacity-50"
						:disabled="quantity >= maxQuantityOrderable"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="51"
							height="47"
							viewBox="0 0 51 47"
						>
							<rect
								id="Rectangle_828"
								data-name="Rectangle 828"
								width="51"
								height="47"
								fill="#e3ded0"
								opacity="0.401"
							/>
							<g
								id="Group_766"
								data-name="Group 766"
								transform="translate(-745.246 -927.002) rotate(45)"
							>
								<line
									id="Line_189"
									data-name="Line 189"
									x2="12.628"
									y2="12.628"
									transform="translate(1211.5 121.5)"
									fill="none"
									stroke="#707070"
									stroke-width="1.5"
								/>
								<line
									id="Line_190"
									data-name="Line 190"
									x1="12.628"
									y2="12.628"
									transform="translate(1211.5 121.5)"
									fill="none"
									stroke="#707070"
									stroke-width="1.5"
								/>
							</g>
						</svg>

						<span class="sr-only">{{ incrementQuantityLabel }}</span>
					</button>
				</div>

				<IntBasketButton
					:post-url="addToBasketUrl"
					:product-id="productId"
					:product-reference="productReference"
					:quantity="quantity"
					:disabled="quantity > maxQuantityOrderable || basketButtonDisabled"
					class="disabled:cursor-not-allowed disabled:opacity-50"
					@click="onAddToBasketClick"
					@basket-updated="onBasketUpdatedEvent"
				>
					{{ buttonText }}
				</IntBasketButton>

				<p v-if="basketError" class="clear-both text-red">{{ basketError }}</p>
			</div>
			<p v-else-if="currentOrderQuantity > 0" class="text-red">
				All available stock in basket
			</p>
			<p v-else class="text-red">Out of stock</p>
		</div>
	</div>
</template>

<script>
import IntBasketButton from "../../components/basket/modules/button"

export default {
	name: "IntStoreProductPricing",

	components: { IntBasketButton },

	data() {
		return {
			quantity: null,
			maxQuantityOrderable: 0,
			currentOrderQuantity: 0,
			basketButtonDisabled: false,
			basketError: null,
		}
	},

	props: {
		addToBasketUrl: {
			type: String,
			required: true,
		},
		productId: {
			type: Number,
			required: true,
		},
		productReference: {
			type: String,
			required: true,
		},
		unitPrice: {
			type: Number,
			required: true,
		},
		initialMaxQuantityOrderable: {
			type: Number,
			required: true,
		},
		initialCurrentOrderQuantity: {
			type: Number,
			default: 0,
		},
		initialQuantity: {
			type: Number,
			default: 1,
		},
		itemsPerPack: {
			type: Number,
			required: true,
		},
		wasPrice: { type: Number },
		productVariants: { type: Array },
		sizeSpecification: {
			type: String,
			validator(value) {
				if (!value && !this.hasVariants) {
					console.error("sizeSpecification is required if productVariants is not set")
					return false
				}

				return true
			},
		},
		decrementQuantityLabel: { type: String, default: "Decrement quantity" },
		incrementQuantityLabel: { type: String, default: "Increment quantity" },
		buttonText: { type: String, default: "Add to basket" },
	},

	computed: {
		hasVariants() {
			return this.productVariants && this.productVariants.length > 0
		},

		itemPriceLabel() {
			return `£${(this.unitPrice / this.itemsPerPack).toFixed(2)}`
		},

		multipleItemsPerPack() {
			return this.itemsPerPack > 1
		},

		showPreviousPrice() {
			return this.wasPrice && this.wasPrice > this.unitPrice
		},

		unitPriceClass() {
			if (this.showPreviousPrice) {
				return "text-red"
			}

			return ""
		},
	},

	mounted() {
		this.maxQuantityOrderable = this.initialMaxQuantityOrderable
		this.quantity =
			this.initialQuantity > this.maxQuantityOrderable
				? this.maxQuantityOrderable
				: this.initialQuantity
		this.currentOrderQuantity = this.initialCurrentOrderQuantity
	},

	methods: {
		getProductVariantLabel(productVariant) {
			return `${productVariant.sizeSpecification} - ${productVariant.eliaCode}`
		},

		incrementQuantity() {
			this.quantity++
		},

		decrementQuantity() {
			this.quantity--
		},

		onAddToBasketClick() {
			this.basketButtonDisabled = true
			this.basketError = null
		},

		// Update the quantity values to update stock message
		onBasketUpdatedEvent(error) {
			if (error !== undefined) {
				this.basketError = error
			} else {
				this.maxQuantityOrderable = this.maxQuantityOrderable - this.quantity
				this.currentOrderQuantity += this.quantity
				this.quantity = 1
				this.basketError = null
			}

			this.basketButtonDisabled = false
		},
	},
}
</script>
